import React, { useEffect, useState } from "react";
import { doc, getDoc, collection, query, where, getDocs, orderBy, addDoc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useParams, useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import { generateCertificate } from "../utils/certificateGenerator";

export default function EmployeeView({ id: propId, onBack }) {
  const { id: paramId } = useParams();
  const id = propId || paramId;
  const navigate = useNavigate();
  const [emp, setEmp] = useState(null);

  const [activeTab, setActiveTab] = useState("PROFILE");
  const [trainings, setTrainings] = useState([]);
  const [welfare, setWelfare] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [salaries, setSalaries] = useState([]);

  // Filtering States
  const [attYearFilter, setAttYearFilter] = useState(new Date().getFullYear().toString());
  const [attMonthFilter, setAttMonthFilter] = useState("ALL");
  const [salYearFilter, setSalYearFilter] = useState("ALL");

  const [notes, setNotes] = useState([]);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({ title: "", content: "" });
  const [savingNote, setSavingNote] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchEmployee();
    fetchEmployeeRecords();
  }, [id]);

  const fetchEmployee = async () => {
    const ref = doc(db, "EMPLOYEES", id);
    const snap = await getDoc(ref);
    if (snap.exists()) setEmp(snap.data());
  };

  const toDateObj = (v) => {
    if (!v) return null;
    if (v.toDate && typeof v.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return d.toString() === "Invalid Date" ? null : d;
  };

  const fetchEmployeeRecords = async () => {
    setLoading(true);
    try {
      // Fetch Training Assignments
      const tQ = query(collection(db, "TRAINING_ASSIGNMENTS"), where("employeeId", "==", id));
      const tSnap = await getDocs(tQ);
      const tData = tSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      tData.sort((a, b) => new Date(b.scheduledDate || 0) - new Date(a.scheduledDate || 0));
      setTrainings(tData);

      // Fetch Welfare Logs
      const wQ = query(collection(db, "WELFARE_LOGS"), where("employeeId", "==", id));
      const wSnap = await getDocs(wQ);
      const wData = wSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      wData.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setWelfare(wData);

      // Fetch Attendance
      const aQ = query(collection(db, "attendance"), where("empId", "==", id));
      const aSnap = await getDocs(aQ);
      const aData = aSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      aData.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      setAttendance(aData);

      // Fetch Salary History
      const sQ = query(collection(db, "salary"), where("empId", "==", id));
      const sSnap = await getDocs(sQ);
      const sData = sSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      sData.sort((a, b) => (b.year === a.year) ? (b.month - a.month) : (b.year - a.year));
      setSalaries(sData);

      // Fetch Employee Notes
      const nQ = query(collection(db, "EMPLOYEE_NOTES"), where("employeeId", "==", id));
      const nSnap = await getDocs(nQ);
      const nData = nSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort in memory to avoid index requirement
      nData.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setNotes(nData);

    } catch (err) {
      console.error("Detailed Fetch Error:", err);
      toast.error("Failed to fetch records. Check console for index link if needed.");
    } finally {
      setLoading(false);
    }
  };

  // Ensure active tab is valid if emp category changes or on initial load
  useEffect(() => {
    if (emp && emp.category === 'Contract' && ["ATTENDANCE", "SALARY"].includes(activeTab)) {
      setActiveTab("PROFILE");
    }
  }, [emp, activeTab]);

  if (!emp) return <Loader />;

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate("/AdminDashboard");
    }
  };

  const handleOpenNoteModal = (note = null) => {
    if (note) {
      setEditingNote(note);
      setNoteForm({ title: note.title, content: note.content });
    } else {
      setEditingNote(null);
      setNoteForm({ title: "", content: "" });
    }
    setShowNoteModal(true);
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!noteForm.title.trim()) return toast.warning("Title is required");
    setSavingNote(true);
    try {
      const data = {
        title: noteForm.title,
        content: noteForm.content,
        employeeId: id,
        updatedAt: Timestamp.now()
      };
      if (editingNote) {
        await updateDoc(doc(db, "EMPLOYEE_NOTES", editingNote.id), data);
        setNotes(notes.map(n => n.id === editingNote.id ? { ...n, ...data } : n));
        toast.success("Note updated");
      } else {
        const docRef = await addDoc(collection(db, "EMPLOYEE_NOTES"), { ...data, createdAt: Timestamp.now() });
        setNotes([{ id: docRef.id, ...data, createdAt: Timestamp.now() }, ...notes]);
        toast.success("Note added");
      }
      setShowNoteModal(false);
    } catch (err) {
      console.error("Error saving note:", err);
      toast.error("Failed to save note");
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await deleteDoc(doc(db, "EMPLOYEE_NOTES", noteId));
      setNotes(notes.filter(n => n.id !== noteId));
      toast.success("Note deleted");
    } catch (err) {
      console.error("Error deleting note:", err);
      toast.error("Failed to delete note");
    }
  };

  const tabs = [
    { id: "PROFILE", label: "Profile", icon: "bi-person-badge" },
    { id: "ATTENDANCE", label: "Attendance", icon: "bi-calendar-check" },
    { id: "SALARY", label: "Salary", icon: "bi-currency-rupee" },
    { id: "TRAINING", label: "Training", icon: "bi-mortarboard" },
    { id: "SKILLS", label: "Skills", icon: "bi-graph-up-arrow" },
    { id: "WELFARE", label: "Welfare", icon: "bi-heart-pulse" },
    { id: "NOTES", label: "Notes", icon: "bi-journal-text" },
  ].filter(t => {
    if (emp && emp.category === 'Contract') {
      return !["ATTENDANCE", "SALARY"].includes(t.id);
    }
    return true;
  });



  return (
    <div className="container-fluid mt-4 px-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-1">{emp.name}</h3>
          <p className="text-muted mb-0">{emp.designation} | {emp.accid}</p>
        </div>
        <button className="btn btn-secondary btn-sm shadow-sm" onClick={handleBack}>
          <i className="bi bi-chevron-left me-1"></i> Back
        </button>
      </div>

      <ul className="nav nav-tabs mb-4">
        {tabs.map(t => (
          <li className="nav-item" key={t.id}>
            <button
              className={`nav-link ${activeTab === t.id ? "active fw-bold" : "text-muted"}`}
              onClick={() => setActiveTab(t.id)}
            >
              <i className={`bi ${t.icon} me-2`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      <div className="card shadow-sm border-0">
        <div className="card-body p-4">
          {activeTab === "PROFILE" && (
            <>
              <div className="mb-4 pb-2 border-bottom">
                <h6 className="fw-bold"><i className="bi bi-person-badge me-2"></i>Identity Details</h6>
              </div>
              <div className="row g-4 mb-5">
                <div className="col-md-3">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Category</p>
                  <span className={`badge rounded-pill ${emp.category === 'Contract' ? 'bg-info text-dark' : 'bg-primary'}`}>{emp.category || "Regular"}</span>
                </div>
                <div className="col-md-3">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Employee ID</p>
                  <div className="fw-bold">{emp.accid}</div>
                </div>
                <div className="col-md-3">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Salesforce ID</p>
                  <div className="fw-bold">{emp.salesforceId || "N/A"}</div>
                </div>
                <div className="col-md-3">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">System UID</p>
                  <code className="bg-light p-1 rounded small border">{emp.uid}</code>
                </div>
              </div>

              <div className="mb-4 pb-2 border-bottom">
                <h6 className="fw-bold"><i className="bi bi-person-bounding-box me-2"></i>Personal & Work Info</h6>
              </div>
              <div className="row g-4 mb-5">
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Full Name</p>
                  <div className="fw-bold fs-5">{emp.name}</div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Designation</p>
                  <div>{emp.designation}</div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Mobile</p>
                  <div>{emp.mobile}</div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Email</p>
                  <div>{emp.email || "-"}</div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Date of Joining</p>
                  <div>{emp.doj || "-"}</div>
                </div>
                <div className="col-md-4">
                  <p className="text-muted small mb-1 text-uppercase fw-bold">Status</p>
                  <span className={`badge rounded-pill ${emp.status === 'Active' ? 'bg-success' : 'bg-danger'}`}>{emp.status}</span>
                </div>
              </div>

              <div className="mb-4 pb-2 border-bottom">
                <h6 className="fw-bold"><i className="bi bi-paperclip me-2"></i>Documents & Biodata</h6>
              </div>
              <div className="row g-4 mb-5">
                <div className="col-md-4">
                  <div className="card h-100 bg-light border-0 shadow-sm p-3 font-monospace">
                    <p className="text-muted small mb-2 text-uppercase fw-bold">Aadhaar</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{emp.aadhar || "N/A"}</span>
                      {emp.aadharUrl && <a href={emp.aadharUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-info text-white"><i className="bi bi-box-arrow-up-right"></i></a>}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100 bg-light border-0 shadow-sm p-3 font-monospace">
                    <p className="text-muted small mb-2 text-uppercase fw-bold">Driving License</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{emp.dl || "N/A"}</span>
                      {emp.dlUrl && <a href={emp.dlUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-info text-white"><i className="bi bi-box-arrow-up-right"></i></a>}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100 bg-light border-0 shadow-sm p-3 font-monospace">
                    <p className="text-muted small mb-2 text-uppercase fw-bold">PAN Card</p>
                    <div className="d-flex justify-content-between align-items-center">
                      <span>{emp.pan || "N/A"}</span>
                      {emp.panUrl && <a href={emp.panUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-info text-white"><i className="bi bi-box-arrow-up-right"></i></a>}
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card h-100 bg-light border-0 shadow-sm p-3">
                    <p className="text-muted small mb-2 text-uppercase fw-bold">Other / Biodata</p>
                    <div className="text-center py-2">
                      {emp.biodataUrl ? <a href={emp.biodataUrl} target="_blank" rel="noreferrer" className="btn btn-primary w-100">View Biodata</a> : <span className="text-muted small">No file uploaded</span>}
                    </div>
                  </div>
                </div>
                <div className="col-md-8">
                  <div className="card h-100 bg-light border-0 shadow-sm p-3">
                    <p className="text-muted small mb-1 text-uppercase fw-bold">Address</p>
                    <div className="small">{emp.addr || "No address provided"}</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === "ATTENDANCE" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0"><i className="bi bi-calendar-check me-2"></i>Attendance History</h5>
                <div className="d-flex gap-2">
                  <select className="form-select form-select-sm" style={{ width: '120px' }} value={attYearFilter} onChange={e => setAttYearFilter(e.target.value)}>
                    <option value="ALL">All Years</option>
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: '120px' }} value={attMonthFilter} onChange={e => setAttMonthFilter(e.target.value)}>
                    <option value="ALL">All Months</option>
                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row g-3 mb-4">
                {(() => {
                  const filteredAtt = attendance.filter(a => {
                    if (attYearFilter !== "ALL" && !a.date.startsWith(attYearFilter)) return false;
                    if (attMonthFilter !== "ALL" && parseInt(a.date.split("-")[1]) !== parseInt(attMonthFilter)) return false;
                    return true;
                  });
                  const present = filteredAtt.filter(a => a.status === 'P' || (!a.status && a.hours >= 5.5)).length;
                  const half = filteredAtt.filter(a => a.status === 'H' || (!a.status && a.hours >= 4.5 && a.hours < 5.5)).length;
                  const absent = filteredAtt.filter(a => a.status === 'A' || (!a.status && (!a.hours || a.hours < 4.5))).length;

                  return (
                    <>
                      <div className="col-md-4">
                        <div className="card bg-success bg-opacity-10 border-0 p-3 text-center shadow-sm">
                          <small className="text-success fw-bold text-uppercase">Present Days</small>
                          <h3 className="mb-0 fw-bold">{present}</h3>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-warning bg-opacity-10 border-0 p-3 text-center shadow-sm">
                          <small className="text-warning fw-bold text-uppercase">Half Days</small>
                          <h3 className="mb-0 fw-bold">{half}</h3>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card bg-danger bg-opacity-10 border-0 p-3 text-center shadow-sm">
                          <small className="text-danger fw-bold text-uppercase">Absent Days</small>
                          <h3 className="mb-0 fw-bold">{absent}</h3>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {attendance.length === 0 ? (
                <div className="text-center py-5 bg-light rounded shadow-sm">
                  <i className="bi bi-calendar-x display-1 text-light mb-3"></i>
                  <h5>No Attendance Data</h5>
                  <p className="text-muted">No attendance logs found for this employee.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle border shadow-sm">
                    <thead className="table-light">
                      <tr>
                        <th>Date (DD/MM/YYYY)</th>
                        <th>Check-In</th>
                        <th>Check-Out</th>
                        <th className="text-center">Hours</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance
                        .filter(a => {
                          if (attYearFilter !== "ALL" && !a.date.startsWith(attYearFilter)) return false;
                          if (attMonthFilter !== "ALL" && parseInt(a.date.split("-")[1]) !== parseInt(attMonthFilter)) return false;
                          return true;
                        })
                        .map(a => {
                          const ci = toDateObj(a.checkInTime);
                          const co = toDateObj(a.checkOutTime);
                          const [y, m, d] = a.date.split("-");
                          return (
                            <tr key={a.id}>
                              <td className="fw-bold">{`${d}/${m}/${y}`}</td>
                              <td>{ci ? ci.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                              <td>{co ? co.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "-"}</td>
                              <td className="text-center">{a.hours ? `${Number(a.hours).toFixed(2)} h` : "-"}</td>
                              <td className="text-center">
                                <span className={`badge ${a.status === 'P' ? 'bg-success' : a.status === 'H' ? 'bg-warning text-dark' : a.status === 'A' ? 'bg-danger' : (a.hours >= 5.5 ? 'bg-success' : a.hours >= 4.5 ? 'bg-warning text-dark' : 'bg-danger')}`}>
                                  {a.status || (a.hours >= 5.5 ? 'P' : a.hours >= 4.5 ? 'H' : 'A')}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "SALARY" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0"><i className="bi bi-currency-rupee me-2"></i>Salary & Payment History</h5>
                <div>
                  <select className="form-select form-select-sm" style={{ width: '120px' }} value={salYearFilter} onChange={e => setSalYearFilter(e.target.value)}>
                    <option value="ALL">All Years</option>
                    {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-md-6">
                  <div className="card bg-primary bg-opacity-10 border-0 p-3 text-center shadow-sm">
                    <small className="text-primary fw-bold text-uppercase">Base Monthly Salary</small>
                    <h3 className="mb-0 fw-bold">₹ {Number(emp.monthlySalary || 0).toLocaleString()}</h3>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card bg-info bg-opacity-10 border-0 p-3 text-center shadow-sm">
                    <small className="text-info fw-bold text-uppercase">Last Paid Amount</small>
                    <h3 className="mb-0 fw-bold">₹ {salaries.length > 0 ? Number(salaries[0].paidAmount || 0).toLocaleString() : "0"}</h3>
                  </div>
                </div>
              </div>

              {salaries.length === 0 ? (
                <div className="text-center py-5 bg-light rounded shadow-sm">
                  <i className="bi bi-cash-stack display-1 text-light mb-3"></i>
                  <h5>No Salary Records</h5>
                  <p className="text-muted">No monthly salary slips generated yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle border shadow-sm">
                    <thead className="table-light">
                      <tr>
                        <th>Month/Year</th>
                        <th className="text-end">Gross</th>
                        <th className="text-end">Advance</th>
                        <th className="text-end">Net Salary</th>
                        <th className="text-end">Paid</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salaries
                        .filter(s => salYearFilter === "ALL" || s.year === parseInt(salYearFilter))
                        .map(s => (
                          <tr key={s.id}>
                            <td className="fw-bold">{s.monthName} {s.year}</td>
                            <td className="text-end">₹ {Number(s.grossSalary || 0).toLocaleString()}</td>
                            <td className="text-end text-danger">- ₹ {Number(s.advanceAmount || 0).toLocaleString()}</td>
                            <td className="text-end fw-bold">₹ {Number(s.netSalary || 0).toLocaleString()}</td>
                            <td className="text-end text-success">₹ {Number(s.paidAmount || 0).toLocaleString()}</td>
                            <td className="text-center">
                              <span className={`badge ${s.status === 'paid' ? 'bg-success' : s.status === 'failed' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                {(s.status || "pending").toUpperCase()}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "TRAINING" && (
            <div>
              <h5 className="fw-bold mb-3"><i className="bi bi-mortarboard me-2"></i>Training & Certifications</h5>
              {trainings.length === 0 ? (
                <div className="text-center py-5 bg-light rounded">
                  <i className="bi bi-journal-x display-1 text-light mb-3"></i>
                  <h5>No Training Records</h5>
                  <p className="text-muted">This employee hasn't been assigned any training yet.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Training Program</th>
                        <th>Scheduled Date</th>
                        <th>Status</th>
                        <th>Notes</th>
                        <th className="text-center">Certificate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainings.map(t => (
                        <tr key={t.id}>
                          <td className="fw-bold">{t.trainingName}</td>
                          <td>{t.scheduledDate}</td>
                          <td>
                            <span className={`badge ${t.status === 'Completed' ? 'bg-success' : t.status === 'Failed' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                              {t.status}
                            </span>
                          </td>
                          <td className="small text-muted">{t.notes || "-"}</td>
                          <td className="text-center">
                            {t.status === 'Completed' ? (
                              <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => generateCertificate({ ...t, employeeName: emp.name })}
                                title="Download Certificate"
                              >
                                <i className="bi bi-file-earmark-pdf me-1"></i>Get Certificate
                              </button>
                            ) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "SKILLS" && (
            <div className="py-2 text-start">
              <div className="row g-4">
                <div className="col-md-7">
                  <h5 className="fw-bold mb-3"><i className="bi bi-graph-up-arrow me-2 text-primary"></i>Technical Skill Matrix</h5>
                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {trainings.filter(t => t.status === 'Completed').length === 0 ? (
                      <div className="alert alert-light border w-100">
                        <i className="bi bi-info-circle me-2"></i> No technical skills verified yet.
                      </div>
                    ) : (
                      trainings.filter(t => t.status === 'Completed').map(t => (
                        <div className="card border-0 bg-primary bg-opacity-10 text-primary p-2 px-3 rounded shadow-sm d-flex flex-row align-items-center" key={t.id}>
                          <i className="bi bi-patch-check-fill me-2"></i>
                          <span className="fw-bold small">{t.trainingName}</span>
                        </div>
                      ))
                    )}
                  </div>

                  <h5 className="fw-bold mb-3 mt-5"><i className="bi bi-trophy me-2 text-warning"></i>Career Growth Roadmap</h5>
                  <div className="position-relative ps-4 border-start border-2 border-light ms-2 mt-4">
                    {(() => {
                      const completed = trainings.filter(t => t.status === 'Completed').map(t => t.trainingName);
                      const levels = [
                        { name: 'Trainee', req: [] },
                        { name: 'Junior Technician', req: ['Basic AC Service', 'Safety Briefing'] },
                        { name: 'Senior Technician', req: ['AC Installation Mastery', 'Advanced Troubleshooting'] },
                        { name: 'Lead Technician', req: ['Soft Skills', 'Compliance Training'] }
                      ];

                      // Simple level calculation: check if all reqs are met
                      const currentLevelIdx = levels.reduce((acc, lvl, idx) => {
                        const hasAll = lvl.req.every(r => completed.includes(r));
                        return hasAll ? idx : acc;
                      }, 0);

                      return levels.map((lvl, idx) => {
                        const isReached = idx <= currentLevelIdx;
                        const isNext = idx === currentLevelIdx + 1;
                        const missing = lvl.req.filter(r => !completed.includes(r));

                        return (
                          <div className={`mb-4 position-relative ${isReached ? 'text-dark' : 'text-muted opacity-50'}`} key={lvl.name}>
                            <div className={`position-absolute top-0 start-0 translate-middle rounded-circle border border-2 ${isReached ? 'bg-primary border-primary' : 'bg-white border-light'}`} style={{ width: '16px', height: '16px', marginLeft: '-26px' }}></div>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <h6 className={`fw-bold mb-0 ${isReached ? 'text-primary' : ''}`}>{lvl.name}</h6>
                                {isReached && idx === currentLevelIdx && <span className="badge bg-success x-small py-1 px-2 mt-1">CURRENT RANK</span>}
                                {isNext && <span className="text-primary small fw-bold">NEXT TARGET</span>}
                                {!isReached && missing.length > 0 && (
                                  <div className="small mt-1">Requires: {missing.join(", ")}</div>
                                )}
                              </div>
                              {isReached && <i className="bi bi-check-circle-fill text-success"></i>}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>

                <div className="col-md-5">
                  <div className="card border-0 bg-light shadow-sm p-4 h-100">
                    <h6 className="fw-bold mb-3">Skill Insights</h6>
                    <p className="small text-muted mb-4">Levels are automatically promoted when required training milestones are recorded as "Completed" by the training administrator.</p>

                    <div className="mb-4">
                      <label className="small fw-bold text-uppercase text-muted opacity-75">Recent Mastery</label>
                      <div className="fw-bold h4">
                        {trainings.filter(t => t.status === 'Completed')[0]?.trainingName || "No Certifications"}
                      </div>
                    </div>

                    <div className="mt-auto">
                      <div className="alert alert-info border-0 shadow-sm small py-2">
                        <i className="bi bi-lightbulb me-2"></i>
                        Tip: Level up your career by requesting new technical training modules.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "WELFARE" && (
            <div>
              <h5 className="fw-bold mb-3"><i className="bi bi-heart-pulse me-2"></i>Welfare & Safety Logs</h5>
              {welfare.length === 0 ? (
                <div className="text-center py-5 bg-light rounded">
                  <i className="bi bi-shield-check display-1 text-light mb-3"></i>
                  <h5>No Welfare Records</h5>
                  <p className="text-muted">Compliance and safety logs for this employee are empty.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Category</th>
                        <th>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {welfare.map(w => (
                        <tr key={w.id}>
                          <td>{w.date}</td>
                          <td>
                            <span className={`badge ${w.type === 'Incident Report' ? 'bg-danger' : 'bg-info text-dark'}`}>
                              {w.type}
                            </span>
                          </td>
                          <td className="small">{w.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "NOTES" && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="fw-bold mb-0"><i className="bi bi-journal-text me-2"></i>Work Journal & Notes</h5>
                <button className="btn btn-primary btn-sm shadow-sm" onClick={() => handleOpenNoteModal()}>
                  <i className="bi bi-plus-lg me-1"></i> Add Note
                </button>
              </div>

              {notes.length === 0 ? (
                <div className="text-center py-5 bg-light rounded shadow-sm">
                  <i className="bi bi-journal-x display-1 text-light mb-3"></i>
                  <h5>No Notes Available</h5>
                  <p className="text-muted">No internal notes recorded for this employee.</p>
                </div>
              ) : (
                <div className="row g-3">
                  {notes.map(n => (
                    <div className="col-md-6" key={n.id}>
                      <div className="card h-100 shadow-sm border-0 bg-light transition-all note-card">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="fw-bold mb-0 text-primary">{n.title}</h6>
                            <div className="d-flex gap-1">
                              <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => handleOpenNoteModal(n)}><i className="bi bi-pencil-square"></i></button>
                              <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => handleDeleteNote(n.id)}><i className="bi bi-trash"></i></button>
                            </div>
                          </div>
                          <p className="small mb-2 text-dark" style={{ whiteSpace: "pre-wrap" }}>{n.content}</p>
                          <div className="text-muted x-small mt-auto pt-2 border-top d-flex justify-content-between">
                            <span><i className="bi bi-clock me-1"></i>{n.createdAt?.toDate ? n.createdAt.toDate().toLocaleDateString() : "Just now"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0 rounded-4">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold text-primary">{editingNote ? "Edit Note" : "Add New Note"}</h5>
                <button type="button" className="btn-close" onClick={() => setShowNoteModal(false)}></button>
              </div>
              <form onSubmit={handleSaveNote}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label small text-muted text-uppercase fw-bold">Title</label>
                    <input
                      type="text"
                      className="form-control border-light shadow-sm"
                      placeholder="e.g., Performance Review Q1"
                      value={noteForm.title}
                      onChange={e => setNoteForm({ ...noteForm, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small text-muted text-uppercase fw-bold">Detailed Content</label>
                    <textarea
                      className="form-control border-light shadow-sm"
                      rows="5"
                      placeholder="Enter internal notes, appreciation, or feedback..."
                      value={noteForm.content}
                      onChange={e => setNoteForm({ ...noteForm, content: e.target.value })}
                      style={{ resize: "none" }}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0">
                  <button type="button" className="btn btn-light rounded-pill px-4" onClick={() => setShowNoteModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary rounded-pill px-4 shadow-sm" disabled={savingNote}>
                    {savingNote ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                    {editingNote ? "Update Note" : "Save Note"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .note-card:hover { transform: translateY(-3px); }
          .transition-all { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
          .x-small { font-size: 0.75rem; }
        `}
      </style>
    </div>
  );
}
